import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Menu } from "lucide-react";
import { cn } from "../utils/cn";

/** Expanded-region width bounds (px). DEFAULT matches the former fixed `w-64`. */
const DEFAULT_WIDTH = 256;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

/** Base localStorage key; per-side keys are derived (`:sidebar` / `:panel`). */
const DEFAULT_STORAGE_KEY = "micronet-ui:sidebar-panel-layout";

function clampWidth(px: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, px));
}

/**
 * Read a persisted width, falling back to the default on any miss/parse error.
 * A valid stored value is clamped to the current MIN/MAX bounds on read, so
 * shrinking MAX later self-corrects previously persisted out-of-range widths.
 */
function readStoredWidth(storageKey?: string): number {
  if (!storageKey || typeof window === "undefined") return DEFAULT_WIDTH;
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw != null ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? clampWidth(parsed) : DEFAULT_WIDTH;
  } catch {
    // Private mode / storage disabled — fall back to the default width.
    return DEFAULT_WIDTH;
  }
}

/**
 * One collapsible, drag-resizable flanking region (sidebar or panel). The layout
 * owns the chrome — a full-height bar with a centered title and a collapse
 * toggle (with a hover tooltip) on its INNER edge, delimited from the main area
 * by a border.
 * Collapsing hides the content + title, shrinking the region into a slim bar
 * that still holds the toggle. When expanded, an inner-edge handle drags to
 * resize the region (clamped to MIN/MAX); double-clicking it resets to default,
 * and the width persists across reloads via localStorage. The left side uses
 * the dark `sidebar` tone; the right side uses the light `panel` tone.
 */
function CollapsibleRegion({
  side,
  title,
  collapsible,
  storageKey,
  children,
}: {
  side: "left" | "right";
  title?: ReactNode;
  collapsible: boolean;
  storageKey?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const [width, setWidth] = useState(() => readStoredWidth(storageKey));
  const [dragging, setDragging] = useState(false);
  const dragCleanup = useRef<(() => void) | null>(null);
  const collapsed = collapsible && !open;
  const isLeft = side === "left";
  const label = isLeft ? "sidebar" : "panel";

  const persistWidth = useCallback(
    (px: number) => {
      if (!storageKey || typeof window === "undefined") return;
      try {
        window.localStorage.setItem(storageKey, String(px));
      } catch {
        // Storage unavailable (private mode) — resizing still works this session.
      }
    },
    [storageKey],
  );

  // Mirrors the latest width so the drag-end handler persists it without a stale
  // closure.
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const startDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Bind move/up to the window synchronously here rather than in an effect: the
    // pointer routinely leaves the 6px handle mid-drag, and a fast click could
    // release before a deferred effect bound them — either way the drag (and its
    // global cursor + selection lock) would get stuck. Listeners bound now always
    // catch move/up.
    const origin = { x: e.clientX, width };
    setDragging(true);

    const { body } = document;
    const prevSelect = body.style.userSelect;
    const prevCursor = body.style.cursor;
    body.style.userSelect = "none";
    body.style.cursor = "col-resize";

    const onMove = (ev: PointerEvent) => {
      const delta = ev.clientX - origin.x;
      // Left region grows as the handle moves right; the right region mirrors it.
      setWidth(clampWidth(origin.width + (isLeft ? delta : -delta)));
    };
    const onUp = () => {
      dragCleanup.current?.();
      setDragging(false);
      persistWidth(widthRef.current);
    };
    // DOM-only teardown (no React state) — safe to run on unmount mid-drag.
    const teardown = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      body.style.userSelect = prevSelect;
      body.style.cursor = prevCursor;
      dragCleanup.current = null;
    };
    dragCleanup.current = teardown;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const resetWidth = () => {
    setWidth(DEFAULT_WIDTH);
    persistWidth(DEFAULT_WIDTH);
  };

  // If the region unmounts mid-drag, detach the window listeners and restore the
  // page styles so nothing leaks or stays locked.
  useEffect(() => () => dragCleanup.current?.(), []);

  return (
    <aside
      style={collapsed ? undefined : { width }}
      className={cn(
        "relative flex h-full flex-shrink-0 flex-col",
        isLeft
          ? "bg-sidebar text-sidebar-foreground border-sidebar-hover border-r"
          : "bg-panel text-panel-foreground border-border border-l",
      )}
    >
      <div
        className={cn(
          "relative flex h-12 flex-shrink-0 items-center justify-center border-b px-2",
          isLeft ? "border-sidebar-hover" : "border-border",
        )}
      >
        {!collapsed && title && (
          <h2 className="text-sm font-semibold">{title}</h2>
        )}
        {collapsible && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
            title={collapsed ? `Expand ${label}` : `Collapse ${label}`}
            className={cn(
              "rounded p-1.5",
              isLeft ? "hover:bg-sidebar-hover" : "hover:bg-muted",
              !collapsed && (isLeft ? "absolute right-2" : "absolute left-2"),
            )}
          >
            <Menu className="h-4 w-4" />
          </button>
        )}
      </div>
      {!collapsed && (
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      )}
      {collapsible && !collapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${label}`}
          onPointerDown={startDrag}
          onDoubleClick={resetWidth}
          className={cn(
            "absolute inset-y-0 z-10 w-1.5 cursor-col-resize touch-none transition-colors hover:bg-primary/50",
            dragging && "bg-primary/60",
            isLeft ? "right-0 translate-x-1/2" : "left-0 -translate-x-1/2",
          )}
        />
      )}
    </aside>
  );
}

export interface SidebarPanelLayoutProps extends HTMLAttributes<HTMLDivElement> {
  /** Left sidebar content. The layout provides the header + chrome. */
  sidebar?: ReactNode;
  /** Title centered in the sidebar header. */
  sidebarTitle?: ReactNode;
  /** Right panel content. The layout provides the header + chrome. */
  panel?: ReactNode;
  /** Title centered in the panel header. */
  panelTitle?: ReactNode;
  /** Main content area. */
  children: ReactNode;
  /** When true (default), the sidebar and panel collapse to a slim bar via their header toggle and can be drag-resized. */
  collapsible?: boolean;
  /**
   * Base localStorage key for persisting resized widths. Per-side keys are
   * derived (`:sidebar` / `:panel`). Override per app if two consumers can
   * share an origin; the default is safe across distinct origins.
   */
  storageKey?: string;
}

/**
 * Horizontal three-region layout: [sidebar | main (flex-1) | panel].
 *
 * Both flanking regions are rendered by the same `CollapsibleRegion`, so the
 * sidebar and panel share identical chrome (centered title, inner-edge collapse
 * toggle, delimiter, collapse-to-slim, drag-to-resize) — only the tone differs
 * (dark sidebar vs light panel).
 */
export function SidebarPanelLayout({
  sidebar,
  sidebarTitle,
  panel,
  panelTitle,
  children,
  className,
  collapsible = true,
  storageKey = DEFAULT_STORAGE_KEY,
  ...rest
}: SidebarPanelLayoutProps) {
  return (
    <div
      className={cn("flex h-full flex-1 overflow-hidden", className)}
      {...rest}
    >
      {sidebar && (
        <CollapsibleRegion
          side="left"
          title={sidebarTitle}
          collapsible={collapsible}
          storageKey={`${storageKey}:sidebar`}
        >
          {sidebar}
        </CollapsibleRegion>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">{children}</div>

      {panel && (
        <CollapsibleRegion
          side="right"
          title={panelTitle}
          collapsible={collapsible}
          storageKey={`${storageKey}:panel`}
        >
          {panel}
        </CollapsibleRegion>
      )}
    </div>
  );
}
SidebarPanelLayout.displayName = "SidebarPanelLayout";
